import { LODGE_EMAIL, LODGE_EMAIL_HREF, LODGE_PHONE_DISPLAY, LODGE_PHONE_HREF, callAriaLabel } from "@/lib/contact-info";
import { createFileRoute } from "@tanstack/react-router";
import { seoHead } from "@/lib/seo";
import { useState } from "react";
import { LodgeHoursCard } from "@/components/LodgeHours";
import { Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { notifyBookingRequest } from "@/lib/notifications.functions";

export const Route = createFileRoute("/contact")({
  head: () =>
    seoHead({
      path: "/contact",
      title: "Book Your Trip — Coffman Cove Lodging",
      description: "Request dates and details for your stay at Coffman Cove Lodging, Alaska.",
    }),
  component: Contact,
});

function Contact() {
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    const form = e.currentTarget;
    const fd = new FormData(form);
    const str = (k: string) => (fd.get(k) as string | null)?.trim() || null;
    setSubmitting(true);
    try {
      const payload = {
        source: "contact" as const,
        name: str("name") ?? "",
        email: str("email") ?? "",
        arrival_date: str("arrival"),
        party_size: str("group") ? Number(str("group")) : null,
        target_species: str("target"),
        notes: str("notes"),
      };
      const { error: insertErr } = await supabase.from("booking_requests").insert(payload);
      if (insertErr) throw insertErr;
      setSent(true);
      void notifyBookingRequest({
        data: {
          source: "contact",
          name: payload.name,
          email: payload.email,
          arrival: payload.arrival_date,
          partySize: payload.party_size,
          target: payload.target_species,
          notes: payload.notes,
        },
      }).catch(() => {});
    } catch {
      setError(
        `We couldn't send your request. Please call ${LODGE_PHONE_DISPLAY} or email ${LODGE_EMAIL}.`,
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div>
      <section className="bg-[var(--color-deep)] text-white">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 py-16 sm:py-20">
          <p className="uppercase tracking-[0.3em] text-xs text-white/80">Book</p>
          <h1 className="font-display text-4xl sm:text-5xl mt-3 text-balance text-white">Plan your week in the cove.</h1>
          <p className="mt-4 text-white/85">
            Tell us when you'd like to come and what you're hoping to catch — we'll get back to you
            within a day. Call us at{" "}
            <a
              href={LODGE_PHONE_HREF}
              aria-label={callAriaLabel(LODGE_PHONE_DISPLAY)}
              className="inline-flex min-h-11 items-center font-semibold text-white hover:underline"
            >
              {LODGE_PHONE_DISPLAY}
            </a>,
            or email{" "}
            <a href={LODGE_EMAIL_HREF} className="font-semibold text-white hover:underline">{LODGE_EMAIL}</a>.
          </p>
        </div>
      </section>
      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-16 sm:py-20">



      <section aria-labelledby="contact-hours-heading" className="mt-2 mb-10">
        <h2
          id="contact-hours-heading"
          className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground"
        >
          <Clock aria-hidden className="h-3.5 w-3.5" /> Lodge Hours
        </h2>
        <LodgeHoursCard variant="light" showContactLink={false} />
      </section>

      {sent ? (
        <div className="mt-10 rounded-lg border border-[var(--color-spruce)] bg-[var(--color-parchment)] p-8 text-center">
          <p className="font-display text-2xl">Thanks — your message is on its way.</p>
          <p className="mt-2 text-muted-foreground">We'll reply within 24 hours.</p>
        </div>
      ) : (
        <form
          className="mt-10 grid gap-5"
          onSubmit={handleSubmit}
        >
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Name" name="name" required />
            <Field label="Email" name="email" type="email" required />
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Arrival date" name="arrival" type="date" />
            <Field label="Group size" name="group" type="number" min={1} />
          </div>
          <label className="grid gap-1.5">
            <span className="text-sm font-medium">What are you fishing for?</span>
            <select
              name="target"
              className="rounded-md border border-input bg-background px-3 py-2"
            >
              <option>Kings & Coho</option>
              <option>Halibut</option>
              <option>Mixed bag</option>
              <option>Not sure yet</option>
            </select>
          </label>
          <label className="grid gap-1.5">
            <span className="text-sm font-medium">Anything else?</span>
            <textarea
              name="notes"
              rows={4}
              className="rounded-md border border-input bg-background px-3 py-2"
            />
          </label>
          {error && (
            <p role="alert" className="text-sm font-medium text-[var(--color-ember)]">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={submitting}
            className="justify-self-start disabled:opacity-60 rounded-md bg-[var(--color-ember)] px-6 py-3 text-sm font-semibold uppercase tracking-wider text-[var(--color-ember-foreground)] hover:opacity-90"
          >
            {submitting ? "Sending…" : "Send request"}
          </button>
        </form>
      )}
      </div>
    </div>
  );
}


function Field(props: React.InputHTMLAttributes<HTMLInputElement> & { label: string }) {
  const { label, ...rest } = props;
  return (
    <label className="grid gap-1.5">
      <span className="text-sm font-medium">{label}</span>
      <input
        {...rest}
        className="rounded-md border border-input bg-background px-3 py-2"
      />
    </label>
  );
}
